/* --------------------------------------------------------------------------
   UF PDP — CLICK TO ZOOM

   Product photos on the rebuilt PDP were plain <img> in a <div>: nothing to
   click, nothing to focus, no lightbox. This adds one.

   IT REUSES THE THEME'S PHOTOSWIPE. The theme already ships photoswipe.min.js,
   already declares it in the importmap, and theme.css already carries the pswp
   styles for the stock gallery. Bundling a second lightbox would mean a second
   library, a second set of styles and two different zoom behaviours on one
   site — so this imports the one that is already there.

   A MODULE, NOT AN IIFE. uf-pdp.js is a classic script and cannot use the
   importmap. This is a separate file purely so it can say `import`.

   ONE SOURCE OF TRUTH FOR THE IMAGES. The desktop grid is in the DOM at every
   width — mobile just hides it with CSS — so both the grid buttons and the
   mobile hero build their slides from the same set of grid buttons. That keeps
   the two galleries from drifting apart, and means the mobile hero opens at the
   right slide without duplicating any data.
-------------------------------------------------------------------------- */
import { PhotoSwipeLightbox } from 'vendor';

if (!window.__ufPdpZoomBound) {
  window.__ufPdpZoomBound = true;

  const cells = (root) =>
    Array.from((root || document).querySelectorAll('[data-uf-zoom]'));

  /* PhotoSwipe needs real pixel dimensions to size the slide before the image
     loads. Falling back to the rendered image stops a missing attribute
     collapsing a slide to nothing. */
  function slideFrom(button) {
    const img = button.querySelector('img');
    const w = parseInt(button.dataset.zoomW, 10);
    const h = parseInt(button.dataset.zoomH, 10);
    return {
      src: button.dataset.zoomSrc || (img && img.currentSrc) || (img && img.src),
      width: w || (img && img.naturalWidth) || 1200,
      height: h || (img && img.naturalHeight) || 1200,
      alt: (img && img.alt) || '',
      /* Lets PhotoSwipe animate out of the thumbnail rather than fading in from
         nowhere, and gives it a low-res placeholder while the full file loads. */
      thumbnailElement: img || undefined,
      msrc: (img && img.currentSrc) || (img && img.src),
    };
  }

  function build(root) {
    const buttons = cells(root);
    if (!buttons.length) return;

    const lightbox = new PhotoSwipeLightbox({
      pswpModule: () => import('photoswipe'),

      /* OUR OWN ROOT CLASS. The theme ships only part of PhotoSwipe's CSS —
         enough for its own gallery, which replaces the arrows with a custom
         pagination widget, so .pswp__button--arrow has no styles at all and the
         two arrows pile up unstyled in the top-left corner. This class scopes
         the missing chrome to this lightbox without touching the theme's. */
      mainClass: 'uf-pswp',

      /* Without padding the image is fitted edge to edge and reads as filling
         the screen rather than sitting on it. */
      padding: { top: 64, bottom: 64, left: 72, right: 72 },

      bgOpacity: 1,
      /* Counter and arrows only earn their place with more than one image. */
      counter: buttons.length > 1,
      arrowKeys: buttons.length > 1,
      zoom: true,
    });
    lightbox.addFilter('thumbEl', (thumbEl, data) => data.thumbnailElement || thumbEl);

    /* PhotoSwipe sets role="dialog" but not aria-modal, so a screen reader will
       happily wander out of the lightbox and read the page behind it. Set on
       firstUpdate rather than an animation event, because the animation events
       do not fire in every context. */
    lightbox.on('firstUpdate', () => {
      const el = lightbox.pswp && lightbox.pswp.element;
      if (el) el.setAttribute('aria-modal', 'true');
    });

    lightbox.init();

    const open = (index) => lightbox.loadAndOpen(index, buttons.map(slideFrom));

    buttons.forEach((button, i) => {
      button.addEventListener('click', () => open(i));
    });

    /* The mobile hero is a separate element that mirrors whichever thumb is
       selected, so it opens the slide matching that media id rather than
       always the first. */
    const hero = (root || document).querySelector('[data-uf-zoom-hero]');
    if (hero) {
      hero.addEventListener('click', () => {
        const selected = (root || document).querySelector('[data-uf-thumb].is-selected');
        const id = selected && selected.dataset.mediaId;
        const index = id ? buttons.findIndex((b) => b.dataset.mediaId === id) : 0;
        open(index > -1 ? index : 0);
      });
    }
  }

  const start = () => build(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* The theme editor replaces a section's markup wholesale, which would leave
     the new buttons unbound. */
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-uf-zoom]')) {
      window.__ufPdpZoomBound = false;
      build(event.target);
      window.__ufPdpZoomBound = true;
    }
  });
}
